import React, { useCallback, useEffect, useReducer } from 'react'
import { Router as RouterProvider } from 'react-router-dom'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { FormProvider, useForm } from 'react-hook-form'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { addDays } from 'date-fns'

import { browserHistory } from './services/history'
import {
  context as Context,
  reducer,
  initialState,
  actionsSubject,
  stateSubject,
  setAddress,
  setPostcode
} from './store'
import { theme } from './theme'
import { App } from './app'
import { fields, settings, isDev } from './store/constants'

// Development bundles (bin/dev) pre-fill the booking form so either arm can
// be clicked through in seconds. Switch off for a browser session with
// localStorage.setItem('dev_prefill', 'off') to test the empty-form UX.
const devPrefillOff = (() => { try { return window.localStorage.getItem('dev_prefill') === 'off' } catch (e) { return false } })()
const devTestData = isDev && !devPrefillOff
  ? {
      address: '45',
      postcode: 'SW6 1RE',
      date: addDays(new Date(), 2),
      timeSlot: 'Anytime 8am-6pm',
      carModel: 'BMW 3 Series',
      carColor: 'Black',
      carRegistration: 'AB12 CDE',
      carSize: 'small',
      notes: ''
    }
  : null

export const AppProvider = React.memo(() => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const bookingForm = useForm({
    // Validate on blur (after first touch) so missing/invalid fields surface
    // their error inline as the user fills the form - instead of the onSubmit
    // default that left "Next" silently disabled with no idea what was missing.
    mode: 'onTouched',
    defaultValues: {
      [fields.address]: devTestData?.address || '',
      [fields.postcode]: devTestData?.postcode || '',
      // The one Car location box (lib/car-location.js) holds both.
      [fields.carLocation]: devTestData ? `${devTestData.address}, ${devTestData.postcode}` : '',
      [fields.addressPicked]: false,
      [fields.locationManual]: false,
      [fields.mobile]: '',
      [fields.homeAddress]: '',
      [fields.date]: devTestData?.date || null,
      [fields.timeSlot]: devTestData?.timeSlot || '',
      [fields.carMake]: devTestData?.carMake || '',
      [fields.carModel]: devTestData?.carModel || '',
      [fields.carColor]: devTestData?.carColor || '',
      [fields.carRegistration]: devTestData?.carRegistration || '',
      [fields.carSize]: devTestData?.carSize || null,
      [fields.notes]: devTestData?.notes || '',
      [fields.agentTip]: false,
      [fields.congestionZone]: false
    }
  })

  const dispatchOverride = useCallback(
    (...args) => {
      dispatch(...args)
      actionsSubject.next(...args)
    },
    [dispatch]
  )

  useEffect(() => {
    // In development, pre-fill the store so the wizard can be clicked through.
    if (devTestData) {
      console.log('[DEV] Pre-filling booking form with test data')
      dispatchOverride(setAddress(devTestData.address))
      dispatchOverride(setPostcode(devTestData.postcode))
    }
  }, [])

  useEffect(() => {
    stateSubject.next(state)
  }, [state])

  return (
    <GoogleOAuthProvider clientId={settings.googleAppId}>
      <Context.Provider value={{ state, dispatch: dispatchOverride }}>
        <MuiThemeProvider theme={theme}>
          <RouterProvider history={browserHistory}>
            <FormProvider {...bookingForm}>
              <App />
            </FormProvider>
          </RouterProvider>
        </MuiThemeProvider>
      </Context.Provider>
    </GoogleOAuthProvider>
  )
})
